import PropTypes from 'prop-types';

/**
 * Displays a single badge with its icon and info
 */
function BadgeItem({ badge, definition, earned }) {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`
        bg-puzzle-background rounded-xl p-4 text-center transition-all
        ${earned ? 'ring-2 ring-puzzle-blue' : 'opacity-50 grayscale'}
      `}
    >
      <div className="text-4xl mb-2">{definition?.icon || '🏅'}</div>
      <div className="font-medium text-puzzle-text text-sm">
        {definition?.name || badge.id}
      </div>
      <div className="text-xs text-puzzle-text-secondary mt-1">
        {definition?.description || ''}
      </div>
      {earned && badge.unlockedAt && (
        <div className="text-xs text-puzzle-blue mt-2">
          Earned
          {' '}
          {formatDate(badge.unlockedAt)}
        </div>
      )}
    </div>
  );
}

BadgeItem.propTypes = {
  badge: PropTypes.shape({
    id: PropTypes.string.isRequired,
    unlockedAt: PropTypes.string,
  }).isRequired,
  definition: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
  }),
  earned: PropTypes.bool,
};

BadgeItem.defaultProps = {
  definition: null,
  earned: false,
};

/**
 * Displays all badges with earned status
 */
function BadgeDisplay({ definitions, earned }) {
  // Create a map of earned badges for quick lookup
  const earnedMap = {};
  earned.forEach((badge) => {
    earnedMap[badge.id] = badge;
  });

  // Get all badge definitions as an array
  const allBadges = Object.values(definitions || {});

  if (allBadges.length === 0) {
    return (
      <div className="text-puzzle-text-secondary text-center py-4">
        No badges available yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {allBadges.map((definition) => {
        const isEarned = !!earnedMap[definition.id];
        const badge = earnedMap[definition.id] || { id: definition.id };

        return (
          <BadgeItem
            key={definition.id}
            badge={badge}
            definition={definition}
            earned={isEarned}
          />
        );
      })}
    </div>
  );
}

BadgeDisplay.propTypes = {
  definitions: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  earned: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      unlockedAt: PropTypes.string,
    }),
  ),
};

BadgeDisplay.defaultProps = {
  definitions: {},
  earned: [],
};

export default BadgeDisplay;
